import React, { useState, useRef, useEffect } from 'react';
import { Scatter } from 'react-chartjs-2';
import { Chart as ChartJS, LinearScale, PointElement, Tooltip, Legend, ChartOptions } from 'chart.js';
import { useGetHiringApplicationsGraphData } from 'src/hooks/campusHiring.hooks';
import { useParams, useSearchParams } from 'react-router-dom';
import { CampusApplicationDocument, SkillGroupType } from 'types';
import NewCreatingPositionLoader from '../position/NewCreatingPositionLoader';
import { getSkillColor } from '../profile/ProfileTechnicalScorecard';

// Register the plugin
interface GraphDataItem {
	name: string;
	level?: number;
	imageUrl: string;
	passingYear: number;
	profileLinkUrl: string;
	imgElement: HTMLCanvasElement;
	skills: { name: string; color: string }[];
	position: { x: number; y: number };
}

// Register Chart.js components
ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

// Define column colors
const columnColors = {
	first: 'rgba(255, 229, 229, 0.5)', // Leftmost
	second: 'rgba(255, 248, 229, 1)', // Second
	third: 'rgba(155, 233, 168, 0.1)', // Third
	fourth: 'rgba(155, 233, 168, 0.2)', // Rightmost
};

const getQuadrantPosition = (level: number) => {
	// - ≥ 800: Excellent Match
	// - ≥ 500 && < 800: Good Match
	// - ≥ 300 && < 500: Average Match
	// - < 300 : Poor Match

	if (level >= 800) {
		return { min: 75, max: 100 };
	} else if (level >= 500 && level < 800) {
		return { min: 50, max: 75 };
	} else if (level >= 300 && level < 500) {
		return { min: 25, max: 50 };
	} else {
		return { min: 0, max: 25 };
	}
};

export const createCircularImage = (url: string): Promise<HTMLCanvasElement> => {
	return new Promise(resolve => {
		const canvas = document.createElement('canvas');
		const size = 48; // Size of the circular image
		canvas.width = size;
		canvas.height = size;
		const ctx = canvas.getContext('2d');

		const img = new Image();
		img.src = url;
		img.onload = () => {
			if (ctx) {
				ctx.beginPath();
				ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
				ctx.closePath();
				ctx.clip();
				// Calculate dimensions to cover the entire circle
				const scale = Math.max(size / img.width, size / img.height);
				const x = (size - img.width * scale) / 2;
				const y = (size - img.height * scale) / 2;

				ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
			}
			resolve(canvas);
		};
	});
};

const SkillsMatchGraph = () => {
	const { id } = useParams();
	const [searchParams] = useSearchParams();
	const [graphData, setGraphData] = useState<GraphDataItem[]>([]);
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
	const top = searchParams.get('top') ? Number(searchParams.get('top')) : 10;

	// Add ref for chart instance
	const chartRef = useRef<any>(null);

	// Add cleanup effect when limit changes
	useEffect(() => {
		return () => {
			if (chartRef.current) {
				chartRef.current.destroy();
				chartRef.current = null;
			}
		};
	}, [top]); // Dependency on top/limit

	// Define the options with quadrant styling and labels
	const options: ChartOptions<'scatter'> = {
		responsive: true,
		animation: {
			duration: 0, // Disable animations for better performance
		},
		elements: {
			point: {
				radius: 6,
				hitRadius: 22,
				hoverRadius: 24,
				borderWidth: 6,
			},
		},
		scales: {
			x: {
				min: 0,
				max: 100,
				grid: {
					color: 'transparent',
				},
				title: {
					display: true,
					text: 'Candidate Fit →',
					color: '#28293D',
					font: {
						size: 12,
						style: 'italic',
						family: 'Inter, sans-serif',
					},
				},
				ticks: {
					stepSize: 2,
					display: false,
				},
			},
			y: {
				min: 0,
				max: 10,
				grid: {
					color: 'transparent',
				},
				title: {
					display: false,
					text: 'Skill Proficiency →',
					color: '#28293D',
					font: {
						size: 12,
						style: 'italic',
						family: 'Inter, sans-serif',
					},
				},
				ticks: {
					display: false,
					stepSize: 2,
				},
				border: {
					display: false,
				},
			},
		},
		plugins: {
			tooltip: {
				enabled: false,
				external: function (context) {
					const tooltipModel = context.tooltip;

					if (tooltipModel.opacity === 0) {
						// Remove tooltip from DOM when not hovering
						const existingTooltipEl = document.getElementById('chartjs-tooltip');
						if (existingTooltipEl && !existingTooltipEl.matches(':hover')) {
							existingTooltipEl.style.opacity = '0';
							document.body.removeChild(existingTooltipEl);
						}
						return;
					}

					// Create tooltip element if it doesn't exist
					let tooltipEl = document.getElementById('chartjs-tooltip');
					if (!tooltipEl) {
						tooltipEl = document.createElement('div');
						tooltipEl.id = 'chartjs-tooltip';
						tooltipEl.innerHTML = '<div></div>';
						document.body.appendChild(tooltipEl);
					}

					const dataIndex = tooltipModel.dataPoints?.[0]?.dataIndex;

					const dataPoint = graphData[dataIndex];

					const innerHtml = /* HTML */ `
						<div class="bg-white p-2 rounded-lg shadow-lg border border-[#E4E4EB] max-w-[240px]">
							<div class="flex items-center gap-3 mb-2">
								<img src="${dataPoint.imageUrl}" class="w-10 h-10 rounded-full" />
								<div>
									<div class="font-semibold text-base text-gray-800">${dataPoint.name}</div>
									${process.env.REACT_APP_NODE_ENV !== 'production'
										? `<div class="text-xs text-gray-500">Score: ${Math.round(dataPoint?.level || 0)}</div>`
										: ''}
								</div>
							</div>
							<div>
								<div class="flex flex-wrap gap-1">
									${dataPoint.skills
										.map(el => {
											return /* HTML */ `<div
												class="text-[#555770] flex items-center gap-1 text-[8.167px] text-center font-[500] leading-[11.667px] border-[0.635px] bg-white border-[#E7E7EE] rounded-[10.162px] px-1"
											>
												<span
													class="rounded-full min-w-[5.385px] min-h-[5.385px]"
													style="background-color: ${el.color || '#555770'}"
												>
												</span>
												${el.name}
											</div>`;
										})
										.join('')}
								</div>
								<div
									class="mt-2 text-xs text-blue-600 cursor-pointer"
									onclick="window.open('${dataPoint.profileLinkUrl}', '_blank')"
								>
									Click to view profile →
								</div>
							</div>
						</div>
					`;

					tooltipEl.querySelector('div')!.innerHTML = innerHtml;

					// Set initial styles
					tooltipEl.style.opacity = '1';
					tooltipEl.style.position = 'fixed';
					tooltipEl.style.pointerEvents = 'auto';
					tooltipEl.style.transition = 'all 0.2s ease';
					tooltipEl.style.zIndex = '1000';

					// Get positions and dimensions
					const position = context.chart.canvas.getBoundingClientRect();
					const tooltipRect = tooltipEl.getBoundingClientRect();
					const chartRect = context.chart.canvas.getBoundingClientRect();

					// Calculate initial positions
					let positionX = position.left + tooltipModel.caretX;
					let positionY = position.top + tooltipModel.caretY;

					// Get available space in each direction
					const spaceAbove = positionY - tooltipRect.height - chartRect.top;
					const spaceBelow = chartRect.bottom - positionY;
					const spaceLeft = positionX - tooltipRect.width / 2 - chartRect.left;
					const spaceRight = chartRect.right - (positionX + tooltipRect.width / 2);

					// Determine best position
					let transform = '';

					// Horizontal positioning
					if (spaceLeft < 0) {
						positionX = chartRect.left + tooltipRect.width / 2 + 10;
					} else if (spaceRight < 0) {
						positionX = chartRect.right - tooltipRect.width / 2 - 10;
					}

					// Vertical positioning
					if (spaceAbove < 0 && spaceBelow > tooltipRect.height) {
						// Show below if not enough space above but enough below
						positionY = positionY + 10;
						transform = 'translate(-50%, 0)';
					} else {
						// Default to above
						transform = 'translate(-50%, -100%)';
						positionY = positionY - 10;
					}

					// Apply positions
					tooltipEl.style.left = `${positionX}px`;
					tooltipEl.style.top = `${positionY}px`;
					tooltipEl.style.transform = transform;

					// Handle mouse leave
					tooltipEl.onmouseleave = () => {
						tooltipEl!.style.opacity = '0';
						tooltipEl!.style.pointerEvents = 'none';
					};
				},
			},
			legend: {
				display: false,
			},
		},
		onClick: (event, elements) => {
			if (elements.length > 0) {
				const index = elements[0].index;
				const candidate = graphData[index];
				window.open(candidate.profileLinkUrl, '_blank');
			}
		},
	};

	// Custom plugin to draw quadrant backgrounds and images
	const columnPlugin = {
		id: 'columnPlugin',
		beforeDraw: (chart: any) => {
			// Comprehensive safety checks
			if (!chart?.ctx || !chart?.chartArea || !chart?.data?.datasets?.[0]?.data) {
				console.log('Chart not ready yet', chart);
				return;
			}

			try {
				const { ctx, chartArea } = chart;
				const { top, bottom, left, right, width, height } = chartArea;

				// Additional safety checks for dimensions
				if (!width || !height || !left || !top || !bottom) {
					console.log('Chart dimensions not ready');
					return;
				}

				const columnWidth = width / 4;

				// Initialize context state
				let contextSaved = false;

				try {
					ctx.save();
					contextSaved = true;

					// Draw the four columns
					[columnColors.first, columnColors.second, columnColors.third, columnColors.fourth].forEach((color, index) => {
						ctx.fillStyle = color;
						ctx.fillRect(left + columnWidth * index, top, columnWidth, height);
					});

					// Draw column labels
					ctx.font = '600 12px Inter, sans-serif';
					ctx.fillStyle = '#7777E7';
					ctx.textAlign = 'center';

					const labels = ['Low Match', 'Moderate Match', 'Strong Match', 'Excellent Match'];
					labels.forEach((label, index) => {
						ctx.fillText(label, left + columnWidth * index + columnWidth / 2, bottom - 20);
					});

					// Safety check for graphData
					if (!graphData || !Array.isArray(graphData)) {
						throw new Error('Graph data not available');
					}
				} finally {
					// Only restore if we successfully saved
					if (contextSaved) {
						try {
							ctx.restore();
						} catch (error) {
							console.error('Error restoring context:', error);
						}
					}
				}
			} catch (error) {
				console.error('Error in column plugin:', error);
			}
		},
	};

	// Modify the position generation logic to be more efficient
	const generatePositions = async (
		candidates: any[],
		getQuadrantPosition: (level: number) => { min: number; max: number },
	) => {
		const gridSize = 0.5; // Size of each grid cell
		const usedCells = new Set<string>();

		const positionPromises = await Promise.all(
			candidates.map(async ele => {
				const xPosition = getQuadrantPosition(Number((ele.application as CampusApplicationDocument)?.level) || 0);
				const columnWidth = xPosition.max - xPosition.min;

				// Try to find an available cell
				let position;
				let cellKey;
				let attempts = 0;
				const maxAttempts = 10; // Reduced maximum attempts

				do {
					const x = Math.random() * columnWidth + xPosition.min;
					const y = Math.random() * 10; // 0 to 10 range

					// Round to grid
					const cellX = Math.floor(x / gridSize);
					const cellY = Math.floor(y / gridSize);
					cellKey = `${cellX},${cellY}`;

					if (!usedCells.has(cellKey) || attempts >= maxAttempts) {
						position = { x, y };
						usedCells.add(cellKey);
						break;
					}

					attempts++;
				} while (attempts < maxAttempts);

				// If no free cell found, slightly offset from last position
				if (!position) {
					position = {
						x: xPosition.min + columnWidth / 2 + (Math.random() - 0.5) * 2,
						y: 5 + (Math.random() - 0.5) * 2,
					};
				}

				const img =
					ele.profile.proctoringImageUrl ||
					'https://masai-website-images.s3.ap-south-1.amazonaws.com/svgviewer_png_output_26_b364f6620b.png';
				const imgElement = await createCircularImage(img); // Wait for the image to load

				return {
					name: ele.name,
					imageUrl: img,
					imgElement: imgElement,
					passingYear: ele.profile.gradYear,
					profileLinkUrl: `/profile/${ele.handler}`,
					level: (ele.application as CampusApplicationDocument)?.level,
					skills:
						(ele.application as CampusApplicationDocument)?.skillRatings?.map(item => ({
							name: item.skill.name,
							color: getSkillColor(item.skill.skillGroupType as SkillGroupType),
						})) || [],
					position,
				};
			}),
		);

		return positionPromises;
	};

	const { isFetching: applicationLoading, isLoading } = useGetHiringApplicationsGraphData({
		page: 0,
		limit: [10, 20, 50].includes(top) ? top : 10,
		hiringAssessment: id || '',
		sort: 'level',
		onComplete: async data => {
			// Destroy existing chart before updating data
			if (chartRef.current) {
				chartRef.current.destroy();
				chartRef.current = null;
			}

			// Generate positions and wait for all images to load
			const newGraphData = await generatePositions(data.campusHiringApplications, getQuadrantPosition);
			setGraphData((newGraphData as unknown as any) || []);
		},
	});

	console.log('graphData:', graphData);

	if (isLoading || applicationLoading) {
		return (
			<div className="mt-[22px] flex items-center justify-center h-[50vh]">
				<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[30%] mx-auto" />
			</div>
		);
	}

	return (
		<div className="w-full mx-auto bg-white rounded-[8px] px-4">
			{isLoading ? (
				<div>loading</div>
			) : (
				<Scatter
					ref={ref => {
						if (ref) {
							chartRef.current = ref;
						}
					}}
					data={{
						labels: ['Candidate Skill Match Analysis'],
						datasets: [
							{
								pointRadius: 10,
								pointStyle: graphData?.map(candidate => candidate?.imgElement) || [],
								label: 'Candidate Skill Match Analysis',
								data: graphData.map(candidate => candidate?.position) || [],
								clip: false,
							},
						],
					}}
					options={options}
					plugins={[columnPlugin]}
				/>
			)}
		</div>
	);
};

export default SkillsMatchGraph;
